import { useState, useEffect, useCallback } from 'react';

export const useServiceWorkerUpdate = () => {
  const [updateState, setUpdateState] = useState({
    updateAvailable: false,
    isUpdating: false,
    newWorker: null,
    showPrompt: false
  });

  const updateAvailable = useCallback((registration) => {
    const newWorker = registration.waiting || registration.installing;
    if (newWorker) {
      setUpdateState(prev => ({
        ...prev,
        updateAvailable: true,
        newWorker,
        showPrompt: true
      }));
    }
  }, []);

  const triggerUpdate = useCallback(() => {
    if (!updateState.newWorker) return Promise.resolve();

    setUpdateState(prev => ({
      ...prev,
      isUpdating: true,
      showPrompt: false
    }));

    // Post message is synchronous, but wrapping in promise for consistency
    return new Promise((resolve) => {
      try {
        updateState.newWorker.postMessage({ type: 'SKIP_WAITING' });
        
        const timeout = setTimeout(() => {
          console.warn('Update timeout - falling back to manual refresh');
          window.location.reload();
        }, 10000);

        const handleUpdateComplete = () => {
          clearTimeout(timeout);
          window.location.reload();
        };

        const updateListener = (event) => {
          if (event.data && event.data.type === 'UPDATE_COMPLETE') {
            navigator.serviceWorker.removeEventListener('message', updateListener);
            handleUpdateComplete();
          }
        };

        navigator.serviceWorker.addEventListener('message', updateListener);
        resolve();

      } catch (error) {
        console.error('Update trigger failed:', error);
        setUpdateState(prev => ({
          ...prev,
          isUpdating: false,
          showPrompt: true
        }));
        resolve();
      }
    });
  }, [updateState.newWorker]);

  const dismissPrompt = useCallback(() => {
    setUpdateState(prev => ({
      ...prev,
      showPrompt: false
    }));
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleControllerChange = () => {
        if (updateState.isUpdating) {
          window.location.reload();
        }
      };

      const handleMessage = (event) => {
        if (event.data && event.data.type === 'UPDATE_COMPLETE') {
          window.location.reload();
        }
      };

      const checkForUpdates = () => {
        navigator.serviceWorker.getRegistration()
          .then(registration => {
            if (registration) {
              if (registration.waiting) {
                updateAvailable(registration);
              }

              registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (newWorker) {
                  newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                      updateAvailable(registration);
                    }
                  });
                }
              });

              if (import.meta.env.PROD) {
                return registration.update();
              }
            }
          })
          .catch(error => {
            console.error('Service worker update check failed:', error);
            if (!navigator.serviceWorker.controller) {
              console.warn('No service worker controller - attempting manual refresh');
              setTimeout(() => window.location.reload(), 1000);
            }
          });
      };

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      navigator.serviceWorker.addEventListener('message', handleMessage);

      checkForUpdates();

      const updateInterval = setInterval(checkForUpdates, 60 * 1000); 

      return () => {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        navigator.serviceWorker.removeEventListener('message', handleMessage);
        clearInterval(updateInterval);
      };
    }
  }, [updateAvailable, updateState.isUpdating]);

  return {
    ...updateState,
    triggerUpdate,
    dismissPrompt
  };
};